import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Lock, UserPlus } from 'lucide-react';
import {
  AddButton, Drawer, Field, LiveScreen, QueryState, Select, StatusPill,
  formatDateTime, type Column,
} from '@/components/live';
import { DataGrid } from '@/components/DataGrid';
import { StatCards, applyStat } from '@/components/StatCards';
import { useRows } from '@/hooks/useResource';
import { callRpc, createRow, saveRow } from '@/api/resources';
import { getSupabase } from '@/lib/supabase';
import { useAuth } from '@/auth/AuthProvider';
import { normaliseIdentifier } from '@/auth/session';
import {
  ACCESS_WRITES, activeAccessTab, canAdministerLogins, canWriteAccess, grantableRoles,
  visibleAccessTabs,
  type AccessTab,
} from '@/lib/accessTabs';
import { RolePermissions } from '@/screens/parts/RolePermissions';

/** The five fixed levels of platform.fn_role_catalogue. Level 0 is the platform's own. */
const ROLE_LEVELS = [
  { tier: 1, label: 'Project admin' },
  { tier: 2, label: 'State admin' },
  { tier: 3, label: 'District admin' },
  { tier: 4, label: 'User' },
];
import { ContactChannelTest } from '@/components/ContactChannelTest';
import { EMPTY_CASCADE, GeographyCascade, cascadeSelection, type CascadeValue } from '@/screens/parts/GeographyCascade';
import { toast } from '@/lib/toast';

interface UserRow {
  id: string;
  full_name: string;
  user_type: string;
  mobile: string | null;
  whatsapp_number: string | null;
  email: string | null;
  role_names: string | null;
  scope_count: number | null;
  has_login: boolean;
  status: string;
  last_login_at: string | null;
}

interface RoleRow { id: string; role_code: string; name: string; tier: number; status: string }
interface ScopeRow { id: string; role_id: string; role_code: string; role_name: string; screen_key: string;
                     record_scope: string; picker_scope: string; status: string }
interface GeoScopeRow { id: string; user_id: string; user_name: string | null; geography_id: string;
                        geography_name: string | null; geography_type: string | null;
                        scope_mode: string; status: string }

const SCOPES = [
  { id: 'own', label: 'Own records only' },
  { id: 'team', label: "Own and the team's" },
  { id: 'all', label: 'All records' },
];

/**
 * `people.users_status_check`, and nothing wider: invited, active, suspended, inactive. What each
 * one means is worth writing down, because three of the four stop a sign-in and they stop it for
 * different reasons.
 */
const USER_STATUSES = [
  { id: 'invited', label: 'Invited - not started yet' },
  { id: 'active', label: 'Active - can sign in' },
  { id: 'suspended', label: 'Suspended - blocked, can be restored' },
  { id: 'inactive', label: 'Inactive - has left' },
];

const SCOPE_MODES = [
  { id: 'exact', label: 'That place only' },
  { id: 'include_children', label: 'That place and everything under it' },
];

/**
 * Access administration: who exists, what they may see, and how far.
 *
 * Three separate questions, deliberately on three tabs, because conflating them is how a
 * permission model becomes unexplainable:
 *   USERS         - who has a login at all. Created by invitation; no self sign-up.
 *   RECORD SCOPE  - Own / Team / All, per role and screen. Picker scope is its own column and
 *                   defaults to All: binding a picker to record scope leaves an `own`-scoped
 *                   role with empty dropdowns, which is a mistake this group has made before.
 *   GEOGRAPHY     - which places a user works in, each optionally including everything below.
 *
 * Nothing here is the enforcement. Every one of these is a row the database reads inside its
 * own policies; this screen is where they are written.
 */
export default function AccessScreen() {
  const { profile } = useAuth();
  const queryClient = useQueryClient();
  // What the reader asked for; `tab` is what they may actually have. A tab they do not hold is
  // never mounted, so its query never runs.
  const [wanted, setWanted] = useState<AccessTab | null>(null);
  const tabs = useMemo(() => visibleAccessTabs(profile), [profile]);
  const tab = activeAccessTab(profile, wanted);
  const [stat, setStat] = useState<string | null>(null);

  // What the database will accept from this reader, asked the way the policies ask it: the grant
  // AND tier 0/1. Activating a login and clearing a PIN ask for the tier alone - that is the
  // server's rule, not an omission.
  const canInvite = canWriteAccess(profile, ACCESS_WRITES.invite.screenKey, ACCESS_WRITES.invite.action);
  const canAddScope = canWriteAccess(profile, ACCESS_WRITES.addScope.screenKey, ACCESS_WRITES.addScope.action);
  const canEditScope = canWriteAccess(profile, ACCESS_WRITES.editScope.screenKey, ACCESS_WRITES.editScope.action);
  const canAssignPlace = canWriteAccess(profile, ACCESS_WRITES.assignPlace.screenKey, ACCESS_WRITES.assignPlace.action);
  const canAdminLogins = canAdministerLogins(profile);
  const canSetPermission = canWriteAccess(profile, ACCESS_WRITES.setPermission.screenKey, ACCESS_WRITES.setPermission.action);
  const canSetStatus = canWriteAccess(profile, ACCESS_WRITES.setUserStatus.screenKey, ACCESS_WRITES.setUserStatus.action);
  const canAddRole = canWriteAccess(profile, 'role.configuration', 'create');
  const canChangeHere = tab === 'users' ? canInvite || canAdminLogins || canSetStatus
    : tab === 'permissions' ? canSetPermission
      : tab === 'scopes' ? canAddScope || canEditScope
        : tab === 'geography' ? canAssignPlace : false;

  // Also on the geography tab: "Assign a place" picks from this list, and it used to open empty
  // unless the reader had visited Users first.
  const users = useRows<UserRow>('users', { orderBy: { column: 'full_name' } }, tab === 'users' || tab === 'geography');
  const roles = useRows<RoleRow>('roles', { orderBy: { column: 'tier' } });
  const scopes = useRows<ScopeRow>('role_screen_scopes', { orderBy: { column: 'screen_key' } }, tab === 'scopes');
  const geoScopes = useRows<GeoScopeRow>('user_geography_scopes', {}, tab === 'geography');

  const [inviting, setInviting] = useState(false);
  const [scopeDrawer, setScopeDrawer] = useState<ScopeRow | 'new' | null>(null);
  const [geoDrawer, setGeoDrawer] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  const [invite, setInvite] = useState({ full_name: '', role_id: '', mobile: '', whatsapp_number: '', email: '' });
  const [scopeForm, setScopeForm] = useState({ role_id: '', screen_key: '', record_scope: 'all', picker_scope: 'all' });
  const [geoForm, setGeoForm] = useState({ user_id: '', scope_mode: 'include_children' });
  const [cascade, setCascade] = useState<CascadeValue>(EMPTY_CASCADE);
  const [statusUser, setStatusUser] = useState<UserRow | null>(null);
  const [addingRole, setAddingRole] = useState(false);
  const [roleForm, setRoleForm] = useState({ name: '', tier: '', description: '' });
  // The five fixed levels (owner: "keep five static role levels"); a new role sits on one below
  // the reader's own. Level 0 is the platform's and is never offered.
  const roleLevels = ROLE_LEVELS.filter((level) => level.tier > (profile?.tier ?? 99))
    .map((level) => ({ id: String(level.tier), label: `${level.label} (tier ${level.tier})` }));
  const [statusForm, setStatusForm] = useState({ status: '', reason: '' });

  const screenKeys = useMemo(() => ([
    'farmer.register', 'plot.register', 'plot.mapping', 'plot.inventory', 'plot.harvest',
    'party.register', 'party.kyc', 'party.consent', 'document.library',
  ].map((key) => ({ id: key, label: key }))), []);

  const roleOptions = (roles.data ?? []).map((role) => ({ id: role.id, label: `${role.name} (tier ${role.tier})` }));
  // Scoping a geography covers every role; INVITING covers only what this person may grant.
  const inviteRoleOptions = grantableRoles(profile, roles.data ?? [])
    .map((role) => ({ id: role.id, label: `${role.name} (tier ${role.tier})` }));
  const userOptions = (users.data ?? []).map((user) => ({ id: user.id, label: user.full_name }));

  // A user row can be active and still have no login: the invitation made the person, the
  // provision-user function makes the account. That gap is its own card.
  const USER_STATS = useMemo(() => [
    { key: 'all', label: 'Users', match: () => true, tone: 'neutral' as const },
    { key: 'invited', label: 'Invited', tone: 'warn' as const, match: (row: UserRow) => row.status === 'invited' },
    { key: 'active', label: 'Active', tone: 'ok' as const, match: (row: UserRow) => row.status === 'active' },
    { key: 'suspended', label: 'Suspended', tone: 'error' as const, match: (row: UserRow) => row.status === 'suspended' },
    { key: 'inactive', label: 'Inactive', tone: 'info' as const, match: (row: UserRow) => row.status === 'inactive' },
    { key: 'no_login', label: 'No login yet', tone: 'warn' as const, match: (row: UserRow) => !row.has_login },
  ], []);

  async function clearPin(row: UserRow) {
    if (!window.confirm(`Clear ${row.full_name}'s approval PIN? They set a new one the next time they approve.`)) return;
    try {
      await callRpc('fn_reset_approval_pin', { p_user_id: row.id });
      toast.success(`${row.full_name}'s approval PIN is cleared.`);
    } catch (caught) {
      toast.error(caught instanceof Error ? caught.message : 'The PIN could not be cleared.');
    }
  }

  const userColumns: Array<Column<UserRow>> = [
    { key: 'full_name', label: 'Name' },
    { key: 'role_names', label: 'Roles', render: (row) => row.role_names ?? 'No role' },
    { key: 'mobile', label: 'Mobile', render: (row) => row.mobile ?? '--' },
    { key: 'email', label: 'Email', render: (row) => row.email ?? '--' },
    { key: 'scope_count', label: 'Places', align: 'right', render: (row) => row.scope_count ?? 0 },
    { key: 'last_login_at', label: 'Last signed in', render: (row) => formatDateTime(row.last_login_at) },
    { key: 'status', label: 'Status', render: (row) => <StatusPill value={row.status} /> },
    {
      key: 'act',
      label: '',
      render: (row) => (!row.has_login && canAdminLogins ? (
        <button onClick={(event) => { event.stopPropagation(); void activate(row); }} type="button">
          Activate login
        </button>
      ) : canAdminLogins && row.has_login ? (
        // A forgotten approval PIN (0065): cleared here, the person sets a new one at their next decision.
        <button onClick={(event) => { event.stopPropagation(); void clearPin(row); }} title="For a forgotten approval PIN" type="button">
          Clear PIN
        </button>
      ) : null),
    },
  ];

  const scopeColumns: Array<Column<ScopeRow>> = [
    { key: 'role_name', label: 'Role' },
    { key: 'screen_key', label: 'Screen', render: (row) => <code>{row.screen_key}</code> },
    // "Sees" used to render a pill reading Active for `all` and PENDING for anything narrower, so
    // a rule correctly scoped to own records announced itself as awaiting approval on the screen
    // that governs permissions. Breadth is not a status. The status column below is the real one.
    { key: 'record_scope', label: 'Sees', render: (row) => SCOPES.find((s) => s.id === row.record_scope)?.label ?? row.record_scope },
    { key: 'picker_scope', label: 'In dropdowns', render: (row) => SCOPES.find((s) => s.id === row.picker_scope)?.label ?? row.picker_scope },
    { key: 'status', label: 'Status', render: (row) => <StatusPill value={row.status} /> },
  ];

  const geoColumns: Array<Column<GeoScopeRow>> = [
    // The view names both; this printed the first eight characters of their ids instead, and the
    // user lookup read a list that is only fetched on the Users tab, so it usually fell through.
    { key: 'user_name', label: 'User', render: (row) => row.user_name ?? '--' },
    { key: 'geography_name', label: 'Place',
      render: (row) => (row.geography_name ? `${row.geography_name}${row.geography_type ? ` (${row.geography_type})` : ''}` : '--') },
    { key: 'scope_mode', label: 'Reach', render: (row) => SCOPE_MODES.find((m) => m.id === row.scope_mode)?.label },
    { key: 'status', label: 'Status', render: (row) => <StatusPill value={row.status} /> },
  ];

  /** Creating the login is a server job: the browser must never hold the service role. */
  async function activate(user: UserRow) {
    try {
      const { data, error: invokeError } = await getSupabase().functions.invoke('provision-user', {
        body: { user_id: user.id },
      });
      if (invokeError) throw invokeError;
      const payload = data as { error?: string; sign_in_with?: string };
      if (payload?.error) throw new Error(payload.error);
      await queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success(`${user.full_name} can now sign in by ${payload.sign_in_with ?? 'code'}.`);
    } catch (caught) {
      toast.error(caught instanceof Error ? caught.message : 'The login could not be created.');
    }
  }

  async function sendInvite() {
    setError(undefined);
    if (!invite.full_name.trim()) { setError('A name is required.'); return; }
    if (!invite.role_id) { setError('Choose a role.'); return; }
    if (!invite.mobile.trim() || !invite.whatsapp_number.trim() || !invite.email.trim()) {
      setError('A mobile number, a WhatsApp number and an email address are all required: each carries messages the others do not.');
      return;
    }
    for (const [key, channel] of [['mobile', 'sms'], ['whatsapp_number', 'whatsapp'], ['email', 'email']] as const) {
      const raw = invite[key].trim();
      const result = normaliseIdentifier(channel, raw);
      if ('error' in result) { setError(result.error); return; }
    }

    setBusy(true);
    try {
      await callRpc('fn_invite_user', {
        p_full_name: invite.full_name.trim(),
        p_role_id: invite.role_id,
        p_mobile: normalise(invite.mobile, 'sms'),
        p_whatsapp_number: normalise(invite.whatsapp_number, 'whatsapp'),
        p_email: normalise(invite.email, 'email'),
        p_invitation_channel: invite.whatsapp_number ? 'whatsapp' : invite.email ? 'email' : 'sms',
      });
      await queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success('Invited. Activate the login when they are ready to start.');
      setInviting(false);
      setInvite({ full_name: '', role_id: '', mobile: '', whatsapp_number: '', email: '' });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'The invitation could not be created.');
    } finally {
      setBusy(false);
    }
  }

  /**
   * Invite, activate, suspend or deactivate (owner, 2026-09-25: "there is no status option for
   * user, but column is there. add to form?").
   *
   * The grid has counted users by status since it was built and nothing could change one, so
   * withdrawing access from somebody who had left meant editing the database. `api.users` is not
   * registered writable, so `api.fn_set_user_status` is the way in; the reason it takes goes to
   * the change history rather than to a column nobody reads.
   */
  async function saveStatus() {
    setError(undefined);
    if (!statusUser) return;
    if (!statusForm.status) { setError('Choose a status.'); return; }
    if (statusForm.status === statusUser.status) { setError(`${statusUser.full_name} is already ${statusUser.status}.`); return; }
    // The server refuses this too. Saying so here means the reader is not told at the end of a
    // form what could have been said at the start of it - and on a single-admin organisation the
    // mistake locks the whole organisation out.
    if (statusUser.id === profile?.user.id && statusForm.status !== 'active') {
      setError('You cannot suspend your own account. Ask another organisation admin.');
      return;
    }
    setBusy(true);
    try {
      await callRpc('fn_set_user_status', {
        p_user_id: statusUser.id,
        p_status: statusForm.status,
        p_reason: statusForm.reason.trim() || null,
      });
      await queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success(`${statusUser.full_name} is ${statusForm.status}.`);
      setStatusUser(null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'The status could not be changed.');
    } finally {
      setBusy(false);
    }
  }

  async function saveScope() {
    setError(undefined);
    if (!scopeForm.role_id || !scopeForm.screen_key) { setError('Choose a role and a screen.'); return; }
    setBusy(true);
    try {
      if (scopeDrawer && scopeDrawer !== 'new') {
        await saveRow('role_screen_scopes', scopeDrawer.id, {
          record_scope: scopeForm.record_scope, picker_scope: scopeForm.picker_scope,
        });
      } else {
        await createRow('role_screen_scopes', {
          role_id: scopeForm.role_id,
          screen_key: scopeForm.screen_key,
          record_scope: scopeForm.record_scope,
          picker_scope: scopeForm.picker_scope,
          status: 'active',
        });
      }
      await queryClient.invalidateQueries({ queryKey: ['role_screen_scopes'] });
      toast.success('Saved. It applies to the next page they load.');
      setScopeDrawer(null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'That could not be saved.');
    } finally {
      setBusy(false);
    }
  }

  /**
   * One row per place chosen, at the deepest level the cascade reached (owner, 2026-09-25:
   * "multiselect of districts or places").
   *
   * Written one at a time rather than in a batch, because each is an independent grant and a
   * place that is already assigned fails on its unique constraint. Failing the whole assignment
   * over one duplicate would be worse than reporting it: the other nine are exactly what was
   * asked for. So they are counted, and what did not go through is named.
   */
  async function saveGeoScope() {
    setError(undefined);
    const chosen = cascadeSelection(cascade);
    if (!geoForm.user_id) { setError('Choose a user.'); return; }
    if (!chosen.ids.length) { setError('Choose at least one place.'); return; }
    setBusy(true);
    const failed: string[] = [];
    let saved = 0;
    try {
      for (const geographyId of chosen.ids) {
        try {
          await createRow('user_geography_scopes', {
            user_id: geoForm.user_id,
            geography_id: geographyId,
            scope_mode: geoForm.scope_mode,
            status: 'active',
          });
          saved += 1;
        } catch (caught) {
          failed.push(caught instanceof Error ? caught.message : 'refused');
        }
      }
      await queryClient.invalidateQueries({ queryKey: ['user_geography_scopes'] });
      if (saved) {
        // The scope cache is refreshed by a trigger on this table, so it is already correct.
        toast.success(`${saved} place${saved === 1 ? '' : 's'} assigned. Their scope is updated immediately.`);
      }
      if (failed.length) {
        setError(`${failed.length} of ${chosen.ids.length} could not be assigned - they may already be: ${failed[0]}`);
        if (!saved) return;
      }
      setGeoDrawer(false);
      setCascade(EMPTY_CASCADE);
    } finally {
      setBusy(false);
    }
  }

  async function addRole() {
    setError(undefined);
    if (!roleForm.tier) { setError('Choose the tier the role sits at.'); return; }
    setBusy(true);
    try {
      await callRpc('fn_create_role', {
        p_name: roleForm.name, p_tier: Number(roleForm.tier), p_description: roleForm.description || null,
      });
      await queryClient.invalidateQueries({ queryKey: ['roles'] });
      toast.success(`${roleForm.name.trim()} added. Pick it on this tab to adjust what it may do.`);
      setAddingRole(false);
      setRoleForm({ name: '', tier: '', description: '' });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'The role could not be added.');
    } finally {
      setBusy(false);
    }
  }

  const activeQuery = tab === 'scopes' ? scopes : tab === 'geography' ? geoScopes : users;

  return (
    <LiveScreen
      actions={
        tab === 'users' && canInvite
          ? <AddButton label="Invite user" onClick={() => { setError(undefined); setInviting(true); }} />
          : tab === 'scopes' && canAddScope
            ? <AddButton label="Add scope rule" onClick={() => {
                setError(undefined);
                setScopeForm({ role_id: '', screen_key: '', record_scope: 'all', picker_scope: 'all' });
                setScopeDrawer('new');
              }} />
            : tab === 'geography' && canAssignPlace
              ? <AddButton label="Assign a place" onClick={() => { setError(undefined); setGeoDrawer(true); }} />
              : tab === 'permissions' && canAddRole
                ? <AddButton label="Add role" onClick={() => { setError(undefined); setAddingRole(true); }} />
                : undefined
      }
      subtitle="Who has a login, what they may see, and where. None of this is the enforcement - it is what the database reads."
      title="Access and scope"
    >
      {tab && !canChangeHere && (
        <p className="offline-banner">
          <Lock size={13} /> Viewing only. Changing this needs an organisation admin who holds the
          permission for it.
        </p>
      )}

      {tabs.length > 1 && (
        <div className="tab-row" role="tablist">
          {tabs.map((entry) => (
            <button aria-selected={tab === entry.id} className={tab === entry.id ? 'active' : ''}
                    key={entry.id} onClick={() => setWanted(entry.id)} role="tab" type="button">
              {entry.label}
            </button>
          ))}
        </div>
      )}

      {/* The route opens on `user.list` or `role.configuration`, so this is reachable only if a
          grant is withdrawn while the screen is open. It says so rather than showing an empty page. */}
      {!tab && (
        <p className="state-note"><Lock size={13} /> Your role does not include access administration.</p>
      )}

      <div style={{ marginTop: 14 }}>
        {/* Its own queries - the catalogue and one role's grants - so it is mounted outside the
            QueryState below, which follows whichever of the other three tabs is open. */}
        {tab === 'permissions' && <RolePermissions />}
        {tab === 'users' && <StatCards active={stat} onSelect={setStat} rows={users.data ?? []} stats={USER_STATS} />}
        {tab && tab !== 'permissions' && <QueryState
          empty={tab === 'scopes'
            ? 'No scope rules. Every role sees all records in its geography until one is added.'
            : tab === 'geography' ? 'Nobody has been assigned a place yet.' : 'No users.'}
          query={activeQuery}
        >
          {tab === 'users' && (
            <DataGrid
              columns={userColumns}
              exportName="users"
              recordNoun="user"
              screenKey="user.list"
              searchPlaceholder="Name, role, mobile or email"
              onRowClick={canSetStatus ? (row) => {
                setError(undefined);
                setStatusForm({ status: row.status, reason: '' });
                setStatusUser(row);
              } : undefined}
              rows={applyStat(users.data ?? [], USER_STATS, stat)}
            />
          )}
          {tab === 'scopes' && (
            <DataGrid
              columns={scopeColumns}
              deleteView="role_screen_scopes"
              exportName="record-scope"
              recordNoun="scope rule"
              screenKey="role.configuration"
              searchPlaceholder="Role or screen"
              onRowClick={canEditScope ? (row) => {
                setError(undefined);
                setScopeForm({ role_id: row.role_id, screen_key: row.screen_key,
                               record_scope: row.record_scope, picker_scope: row.picker_scope });
                setScopeDrawer(row);
              } : undefined}
              rows={scopes.data ?? []}
            />
          )}
          {tab === 'geography' && (
            <DataGrid columns={geoColumns} deleteView="user_geography_scopes" exportName="geography-scope"
                      recordNoun="assignment" rows={geoScopes.data ?? []} screenKey="user.geography.scope"
                      searchPlaceholder="User or place" />
          )}
        </QueryState>}
      </div>

      <Drawer
        footer={
          <>
            <button className="cancel" onClick={() => setInviting(false)} type="button">Cancel</button>
            <button className="primary" disabled={busy} onClick={() => void sendInvite()} type="button">
              <UserPlus size={15} /> {busy ? 'Inviting...' : 'Invite'}
            </button>
          </>
        }
        onClose={() => setInviting(false)}
        open={inviting}
        subtitle="They cannot create an account themselves. This is the only way in."
        title="Invite a user"
      >
        <Field label="Full name" required>
          <input onChange={(event) => setInvite({ ...invite, full_name: event.target.value })} value={invite.full_name} />
        </Field>
        <Field hint="You cannot grant a role with more authority than your own." label="Role" required>
          <Select onChange={(value) => setInvite({ ...invite, role_id: value })} options={inviteRoleOptions} value={invite.role_id} />
        </Field>
        <div className="field-row">
          <Field label="Mobile" required>
            <input inputMode="tel" onChange={(event) => setInvite({ ...invite, mobile: event.target.value })} value={invite.mobile} />
          </Field>
          <Field label="WhatsApp number" required>
            <input inputMode="tel" onChange={(event) => setInvite({ ...invite, whatsapp_number: event.target.value })} value={invite.whatsapp_number} />
          </Field>
        </div>
        <Field hint="All three are required: each carries messages the others do not." label="Email" required>
          <input inputMode="email" onChange={(event) => setInvite({ ...invite, email: event.target.value })} value={invite.email} />
        </Field>
        {error && <p className="login-error" role="alert">{error}</p>}
      </Drawer>

      <Drawer
        footer={
          <>
            <button className="cancel" onClick={() => setAddingRole(false)} type="button">Cancel</button>
            <button className="primary" disabled={busy || !roleForm.name.trim()} onClick={() => void addRole()} type="button">
              {busy ? 'Adding...' : 'Add role'}
            </button>
          </>
        }
        onClose={() => setAddingRole(false)}
        open={addingRole}
        subtitle="A named role on one of the five fixed tiers. The tier decides who may invite into it and whose permissions it may change."
        title="Add a role"
      >
        <Field hint="What people call it, e.g. Nursery supervisor." label="Name" required>
          <input onChange={(event) => setRoleForm({ ...roleForm, name: event.target.value })} value={roleForm.name} />
        </Field>
        <Field hint="It starts with that tier's standard permissions; adjust them here once it is added." label="Tier" required>
          <Select onChange={(value) => setRoleForm({ ...roleForm, tier: value })} options={roleLevels} value={roleForm.tier} />
        </Field>
        <Field label="Description">
          <textarea onChange={(event) => setRoleForm({ ...roleForm, description: event.target.value })} rows={2}
                    value={roleForm.description} />
        </Field>
        {error && <p className="login-error" role="alert">{error}</p>}
      </Drawer>

      <Drawer
        footer={
          <>
            <button className="cancel" onClick={() => setStatusUser(null)} type="button">Cancel</button>
            <button className="primary" disabled={busy} onClick={() => void saveStatus()} type="button">
              {busy ? 'Saving...' : 'Change status'}
            </button>
          </>
        }
        onClose={() => setStatusUser(null)}
        open={statusUser !== null}
        subtitle="Suspending or deactivating stops the next sign-in. It does not delete anything they recorded."
        title={statusUser ? `${statusUser.full_name}'s access` : 'Access'}
      >
        {statusUser && (
          <>
            <Field hint="What it is now." label="Current">
              <p><StatusPill value={statusUser.status} />{statusUser.has_login ? '' : ' - no login has been created yet'}</p>
            </Field>
            <Field label="Change to" required>
              <Select onChange={(value) => setStatusForm({ ...statusForm, status: value })}
                      options={USER_STATUSES} value={statusForm.status} />
            </Field>
            <Field
              hint="Kept in the change history, where an auditor asking why access was withdrawn will look."
              label="Reason"
            >
              <textarea onChange={(event) => setStatusForm({ ...statusForm, reason: event.target.value })}
                        rows={2} value={statusForm.reason} />
            </Field>
          </>
        )}
        {error && <p className="login-error" role="alert">{error}</p>}
      </Drawer>

      <Drawer
        footer={
          <>
            <button className="cancel" onClick={() => setScopeDrawer(null)} type="button">Cancel</button>
            <button className="primary" disabled={busy} onClick={() => void saveScope()} type="button">Save</button>
          </>
        }
        onClose={() => setScopeDrawer(null)}
        open={scopeDrawer !== null}
        subtitle="With no rule, a role sees every record inside its geography."
        title="Record scope"
      >
        <Field label="Role" required>
          <Select disabled={scopeDrawer !== 'new'} onChange={(value) => setScopeForm({ ...scopeForm, role_id: value })}
                  options={roleOptions} value={scopeForm.role_id} />
        </Field>
        <Field label="Screen" required>
          <Select disabled={scopeDrawer !== 'new'} onChange={(value) => setScopeForm({ ...scopeForm, screen_key: value })}
                  options={screenKeys} value={scopeForm.screen_key} />
        </Field>
        <Field hint="What they see in the grid." label="Records they see">
          <Select onChange={(value) => setScopeForm({ ...scopeForm, record_scope: value })} options={SCOPES} value={scopeForm.record_scope} />
        </Field>
        <Field
          hint="Keep this wider than the grid. A role that can only pick its own records cannot reference anything it did not create."
          label="Records they can pick in dropdowns"
        >
          <Select onChange={(value) => setScopeForm({ ...scopeForm, picker_scope: value })} options={SCOPES} value={scopeForm.picker_scope} />
        </Field>
        {error && <p className="login-error" role="alert">{error}</p>}
      </Drawer>

      <Drawer
        footer={
          <>
            <button className="cancel" onClick={() => setGeoDrawer(false)} type="button">Cancel</button>
            <button className="primary" disabled={busy} onClick={() => void saveGeoScope()} type="button">
              {busy ? 'Assigning...' : 'Assign'}
            </button>
          </>
        }
        onClose={() => { setGeoDrawer(false); setCascade(EMPTY_CASCADE); }}
        open={geoDrawer}
        subtitle="Work down from the state. Stop at any level - a district with everything under it covers all its villages."
        title="Assign places"
      >
        <Field label="User" required>
          <Select onChange={(value) => setGeoForm({ ...geoForm, user_id: value })} options={userOptions} value={geoForm.user_id} />
        </Field>
        <GeographyCascade onChange={setCascade} value={cascade} />
        <Field label="Reach">
          <Select onChange={(value) => setGeoForm({ ...geoForm, scope_mode: value })} options={SCOPE_MODES} value={geoForm.scope_mode} />
        </Field>
        {error && <p className="login-error" role="alert">{error}</p>}
      </Drawer>

      {/* Renders nothing below tier 0 - the guard is inside the component, not here. */}
      <ContactChannelTest />
    </LiveScreen>
  );
}

function normalise(raw: string, channel: 'sms' | 'whatsapp' | 'email'): string | null {
  const value = raw.trim();
  if (!value) return null;
  const result = normaliseIdentifier(channel, value);
  return 'value' in result ? result.value : value;
}
